import React from 'react';
import * as Icons from 'lucide-react';
import { DOMAINS } from '../../config/domains';
import { ABLE_COLORS } from '../../constants';

/**
 * Sidebar - Collapsible domain navigation
 * 
 * Desktop: Thin strip on left, expands on hover
 * Mobile: Hidden by default, slides in as overlay
 */
export default function Sidebar({
  enabledDomains,
  currentDomain,
  onSelectDomain,
  expanded,
  pinned,
  onToggleExpanded,
  onSetExpanded,
  onTogglePinned,
  onOpenSettings,
  onOpenDomainSettings,
  isMobile = false,
}) {
  const sidebarWidth = expanded ? 180 : 56;

  // Get icon component by name
  const getIcon = (iconName) => {
    return Icons[iconName] || Icons.Circle;
  };

  // Handle domain click
  const handleDomainClick = (domainId) => {
    onSelectDomain(domainId);
    // Collapse on mobile after selection
    if (isMobile && !pinned) {
      onToggleExpanded();
    }
  };

  // Mobile overlay backdrop
  if (isMobile && expanded) {
    return (
      <>
        {/* Backdrop */}
        <div
          onClick={onToggleExpanded}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 998,
          }}
        />
        {/* Sidebar */}
        <SidebarContent
          enabledDomains={enabledDomains}
          currentDomain={currentDomain}
          onSelectDomain={handleDomainClick}
          expanded={true}
          pinned={pinned}
          onTogglePinned={onTogglePinned}
          onOpenSettings={onOpenSettings}
          onOpenDomainSettings={onOpenDomainSettings}
          getIcon={getIcon}
          isMobile={true}
        />
      </>
    );
  }

  // Mobile collapsed - just a hamburger button (handled by parent)
  if (isMobile) {
    return null;
  }

  // Desktop sidebar
  return (
    <div
      onMouseEnter={() => { if (!pinned && onSetExpanded) onSetExpanded(true); }}
      onMouseLeave={() => { if (!pinned && onSetExpanded) onSetExpanded(false); }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: sidebarWidth,
        background: '#FAFAF9',
        borderRight: `1px solid ${ABLE_COLORS.border}`,
        transition: 'width 0.2s ease',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <SidebarContent
        enabledDomains={enabledDomains}
        currentDomain={currentDomain}
        onSelectDomain={handleDomainClick}
        expanded={expanded}
        pinned={pinned}
        onTogglePinned={onTogglePinned}
        onOpenSettings={onOpenSettings}
        onOpenDomainSettings={onOpenDomainSettings}
        getIcon={getIcon}
        isMobile={false}
      />
    </div>
  );
}

/**
 * SidebarContent - Inner content (shared between desktop and mobile)
 */
function SidebarContent({
  enabledDomains,
  currentDomain,
  onSelectDomain,
  expanded,
  pinned,
  onTogglePinned,
  onOpenSettings,
  onOpenDomainSettings,
  getIcon,
  isMobile,
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: isMobile ? 220 : '100%',
      background: '#FAFAF9',
      position: isMobile ? 'fixed' : 'relative',
      left: 0,
      top: 0,
      bottom: 0,
      zIndex: isMobile ? 999 : 'auto',
      boxShadow: isMobile ? '4px 0 12px rgba(0,0,0,0.1)' : 'none',
    }}>
      {/* Logo area */}
      <div style={{
        padding: expanded ? '16px' : '16px 12px',
        borderBottom: `1px solid ${ABLE_COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minHeight: '56px',
      }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: ABLE_COLORS.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 700,
          fontSize: '16px',
          flexShrink: 0,
        }}>
          M
        </div>
        {expanded && (
          <span style={{
            fontWeight: 600,
            fontSize: '16px',
            color: ABLE_COLORS.textPrimary,
            whiteSpace: 'nowrap',
          }}>
            Mabel
          </span>
        )}
      </div>

      {/* Domain list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px',
      }}>
        {enabledDomains.map(domainId => {
          const domain = DOMAINS[domainId];
          if (!domain) return null;
          
          const Icon = getIcon(domain.icon);
          const isActive = currentDomain === domainId;

          return (
            <button
              key={domainId}
              onClick={() => onSelectDomain(domainId)}
              style={{
                width: '100%',
                padding: expanded ? '10px 12px' : '10px',
                background: isActive ? ABLE_COLORS.primaryLight : 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '4px',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = '#F0F0EE';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <Icon 
                size={20} 
                color={isActive ? ABLE_COLORS.primary : ABLE_COLORS.textSecondary}
                style={{ flexShrink: 0 }}
              />
              {expanded && (
                <span style={{
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? ABLE_COLORS.primary : ABLE_COLORS.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {domain.name}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Bottom section */}
      <div style={{
        borderTop: `1px solid ${ABLE_COLORS.border}`,
        padding: '8px',
      }}>
        {/* Add domain button */}
        <button
          onClick={onOpenDomainSettings}
          style={{
            width: '100%',
            padding: expanded ? '10px 12px' : '10px',
            background: 'transparent',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '4px',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#F0F0EE';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Icons.Plus 
            size={20} 
            color={ABLE_COLORS.textMuted}
            style={{ flexShrink: 0 }}
          />
          {expanded && (
            <span style={{
              fontSize: '14px',
              fontWeight: 500,
              color: ABLE_COLORS.textMuted,
              whiteSpace: 'nowrap',
            }}>
              Add domain
            </span>
          )}
        </button>

        {/* Settings button */}
        <button
          onClick={onOpenSettings}
          style={{
            width: '100%',
            padding: expanded ? '10px 12px' : '10px',
            background: 'transparent',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '4px',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#F0F0EE';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Icons.Settings 
            size={20} 
            color={ABLE_COLORS.textMuted}
            style={{ flexShrink: 0 }}
          />
          {expanded && (
            <span style={{
              fontSize: '14px',
              fontWeight: 500,
              color: ABLE_COLORS.textMuted,
              whiteSpace: 'nowrap',
            }}>
              Settings
            </span>
          )}
        </button>

        {/* Pin button (desktop only) */}
        {expanded && !isMobile && (
          <button
            onClick={onTogglePinned}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: pinned ? ABLE_COLORS.primaryLight : 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (!pinned) {
                e.currentTarget.style.background = '#F0F0EE';
              }
            }}
            onMouseLeave={(e) => {
              if (!pinned) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <Icons.Pin 
              size={20} 
              color={pinned ? ABLE_COLORS.primary : ABLE_COLORS.textMuted}
              style={{ 
                flexShrink: 0,
                transform: pinned ? 'rotate(45deg)' : 'none',
                transition: 'transform 0.15s ease',
              }}
            />
            <span style={{
              fontSize: '14px',
              fontWeight: pinned ? 600 : 500,
              color: pinned ? ABLE_COLORS.primary : ABLE_COLORS.textMuted,
              whiteSpace: 'nowrap',
            }}>
              {pinned ? 'Unpin' : 'Pin sidebar'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
